/**
 * User Transactions Store Slice
 *
 * Zustand slice for user-level transactions page (My Transactions).
 * Handles filtering, pagination, sorting, and token balance for current user.
 * Follows same pattern as organizationTransactionsSlice.
 */

import { clearError as clearOpError, hasOperationLoaded, isOperationLoading } from '@gw/ui';

import type {
  ErrorStates,
  ErrorStatuses,
  LoadedTimestamps,
  LoadingStates,
  TokenTransaction,
  TransactionFilters,
  TransactionPagination,
  TransactionSort,
} from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface UserTransactionsSlice {
  // State properties
  userTransactions: TokenTransaction[];
  userPagination: TransactionPagination;
  userFilters: Omit<TransactionFilters, 'userIds'>; // User-level doesn't filter by user
  userSort: TransactionSort;
  userTokenBalance: number | null;
  userIsLoading: LoadingStates;
  userHasLoaded: LoadingStates;
  userLastLoaded: LoadedTimestamps;
  userErrors: ErrorStates;
  userErrorStatuses: ErrorStatuses;

  // Filter management actions
  setUserFilters: (filters: Partial<Omit<TransactionFilters, 'userIds'>>) => void;
  clearUserFilters: () => void;

  // Sort management actions
  setUserSort: (sort: TransactionSort) => void;

  // Pagination management actions
  setUserPage: (page: number) => void;

  // Utility actions
  clearUserError: (operation?: string) => void;
  checkUserIsLoading: (operation: string) => boolean;
  checkUserHasLoaded: (operation: string) => boolean;
}

// ============================================================================
// Slice Creator
// ============================================================================

export const createUserTransactionsSlice: StateCreator<UserTransactionsSlice> = (set, get) => ({
  // State initialization
  userTransactions: [],
  userPagination: {
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false,
  },
  userFilters: {},
  userSort: {
    field: 'createdAt',
    order: 'desc',
  },
  userTokenBalance: null,
  userIsLoading: {},
  userHasLoaded: {},
  userLastLoaded: {},
  userErrors: {},
  userErrorStatuses: {},

  // Set filters (merges with existing filters)
  setUserFilters: (newFilters: Partial<Omit<TransactionFilters, 'userIds'>>) => {
    const currentState = get();
    set({
      userFilters: {
        ...currentState.userFilters,
        ...newFilters,
      },
      // Reset pagination to first page when filters change
      userPagination: {
        ...currentState.userPagination,
        offset: 0,
      },
    });
  },

  // Clear all filters
  clearUserFilters: () => {
    const currentState = get();
    set({
      userFilters: {},
      // Reset pagination to first page
      userPagination: {
        ...currentState.userPagination,
        offset: 0,
      },
    });
  },

  // Set sort configuration
  setUserSort: (newSort: TransactionSort) => {
    set({ userSort: newSort });
  },

  // Set page number (1-indexed)
  setUserPage: (page: number) => {
    const currentState = get();
    const offset = (page - 1) * currentState.userPagination.limit;
    set({
      userPagination: {
        ...currentState.userPagination,
        offset,
      },
    });
  },

  // Utility actions
  clearUserError: (operation?: string) => {
    const currentState = get();
    if (operation) {
      set({ userErrors: clearOpError(currentState.userErrors, operation) });
    } else {
      set({ userErrors: {} });
    }
  },
  checkUserIsLoading: (operation: string) => isOperationLoading(get().userIsLoading, operation),
  checkUserHasLoaded: (operation: string) => hasOperationLoaded(get().userHasLoaded, operation),
});
